import type { PartnerPermission } from '@/lib/api/api-key-api'
import { TriStateCheckbox } from '@/components/roles/tri-state-checkbox'
import { PARTNER_PERMISSIONS } from '@/lib/api/api-key-api'
import { m } from '@/lib/paraglide/messages'
import { toggleCode, toggleCodes, tristate } from '@/lib/permission-selection'

// Nhãn tiếng Việt của từng quyền partner, theo thứ tự PARTNER_PERMISSIONS.
export function partnerPermissionLabel(code: PartnerPermission): string {
    switch (code) {
        case 'growers.read':
            return m.api_keys_perm_growers()
        case 'areas.read':
            return m.api_keys_perm_areas()
        case 'tea_types.read':
            return m.api_keys_perm_tea_types()
        case 'employees.read':
            return m.api_keys_perm_employees()
        case 'grower_faces.read':
            return m.api_keys_perm_grower_faces()
        case 'care_logs.read':
            return m.api_keys_perm_care_logs()
        case 'money_loop_metrics.read':
            return m.api_keys_perm_money_loop_metrics()
        case 'business_metrics.read':
            return m.api_keys_perm_business_metrics()
    }
}

// Chuyển tập đã chọn về mảng theo đúng thứ tự danh mục (để gửi server ổn định).
export function orderedPartnerPermissions(
    selected: Set<string>,
): PartnerPermission[] {
    return PARTNER_PERMISSIONS.filter((code) => selected.has(code))
}

interface Props {
    id: string
    value: Set<string>
    onChange: (next: Set<string>) => void
    error?: string
    hint?: string
}

// Ô chọn quyền đọc cho khoá partner: một ô "Chọn tất cả" 3 trạng thái và một ô
// tick cho mỗi resource. Lỗi (vd chưa chọn quyền nào) hiện ngay dưới nhóm ô.
export function PartnerPermissionsField({
    id,
    value,
    onChange,
    error,
    hint,
}: Props) {
    const all = [...PARTNER_PERMISSIONS]
    const state = tristate(
        all.filter((code) => value.has(code)).length,
        all.length,
    )
    const errorId = `${id}-error`

    return (
        <fieldset
            className="grid gap-2"
            aria-describedby={error ? errorId : undefined}
            aria-invalid={error ? true : undefined}
        >
            <legend className="text-sm font-medium leading-none">
                {m.api_keys_permissions_label()}
            </legend>
            <p className="text-xs text-muted-foreground">
                {hint ?? m.api_keys_permissions_hint()}
            </p>
            <label className="flex min-h-9 cursor-pointer items-center gap-2 rounded-md px-2 text-sm font-medium hover:bg-muted">
                <TriStateCheckbox
                    value={state}
                    label={
                        state === 'all'
                            ? m.api_keys_permissions_clear()
                            : m.api_keys_permissions_select_all()
                    }
                    onToggle={() => onChange(toggleCodes(value, all))}
                />
                {state === 'all'
                    ? m.api_keys_permissions_clear()
                    : m.api_keys_permissions_select_all()}
            </label>
            <div className="grid gap-1 sm:grid-cols-2">
                {PARTNER_PERMISSIONS.map((code) => (
                    <label
                        key={code}
                        className="flex min-h-9 cursor-pointer items-center gap-2 rounded-md px-2 text-sm hover:bg-muted"
                    >
                        <input
                            type="checkbox"
                            checked={value.has(code)}
                            onChange={() => onChange(toggleCode(value, code))}
                            className="size-4 shrink-0 cursor-pointer accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                        {partnerPermissionLabel(code)}
                    </label>
                ))}
            </div>
            {error && (
                <p id={errorId} className="text-sm text-destructive">
                    {error}
                </p>
            )}
        </fieldset>
    )
}
